<template>
    <el-popover class="factor-tooltip" popper-class="popper-factor">
        <div class="popover-content">
            <span class="display-time" v-if="sleepDetail.length > 0">
                {{ daySlotDisplay }}
            </span>
            <div class="display-detail">
                {{ sleepDetail.join(', ') }}
            </div>
            <div class="display-detail" v-if="sleepStart.length > 0">
                Sleep - {{ sleepStart.map(formatTime).join(', ') }}
            </div>
            <div class="display-detail" v-if="sleepEnd.length > 0">
                Wake-up - {{ sleepEnd.map(formatTime).join(', ') }}
            </div>
        </div>
        <div slot="reference" ref="sCell" class="sleep-cell">
            <span v-for="(sleep, sleepIndex) in sleepStart"
                :key="sleep + '-start-erase' + sleepIndex"
                v-if="isLineEraseBefore" 
                class="line-erase-before"
                :style="{width: getPercentageFromTime(sleep)}"></span>
            <span v-for="(sleep, sleepIndex) in sleepEnd"
                :key="sleep + '-end-erase-' + sleepIndex"
                v-if="isLineEraseAfter"
                class="line-erase-after"
                :style="{marginLeft: getPercentageFromTime(sleep)}"></span>
            <span v-for="(detailVisual, index) in sleepDetailVisual"
                :key="index"
                :class="['detail', detailVisual.detail]"
                :style="{marginLeft: detailVisual.marginLeft}">
                {{ detailVisual.detailInitials }}
            </span>
            <span v-for="(sleep, sleepIndex) in sleepStart"
                :key="sleep + '-start-' + sleepIndex"
                :style="{marginLeft: getPercentageFromTime(sleep)}"
                class="sleep-event sleep-start">
            </span>
            <span  v-for="(sleep, sleepIndex) in sleepEnd"
                :key="sleep + '-end-' + sleepIndex"
                :style="{marginLeft: getPercentageFromTime(sleep)}"
                class="sleep-event sleep-end">
            </span>
        </div>
    </el-popover>
</template>

<script>
import format from '@/module/util/format'

import { getTimeFromIndex } from './util'
/**
 * Stores width of single element representing the factors (i.e. Nap, Caffiene, etc)
 */
const FactorWidth = 16

export default {
    name: 'sleep-cell',

    data() {
        return {
            isMounted: false
        }
    },

    props: {
        sleepDetail: {
            type: Array,
            default: () => []
        },
        daySlot: {
            type: Number
        },
        sleepStart: {
            type: Array,
            default: () => []
        },
        sleepEnd: {
            type: Array,
            default: () => []
        },
        sleepStatus: {
            type: Object,
            default: () => {}
        }
    },

    mounted() {
        this.isMounted = true
    },

    computed: {
        /**
         * Calculates visual data for the sleep details
         */
        sleepDetailVisual() {
            try {
                // Only calculates if component is mounted, becuase $ref in inaccesible before that
                if(!this.isMounted)
                    return []
                // Get total no. of factors and width of the cell
                const totalFactors = this.sleepDetail.length, cellWidth = this.$refs.sCell.clientWidth
                // Loops over sleep details while mapping initials and left margins on CSS grid
                return this.sleepDetail.map((detail, index) => {
                    let marginLeft
                    // If cell width is less than total width of element of all elements than
                    // margin is basically position of element * `FactorWidth`
                    if(totalFactors * FactorWidth <= cellWidth)
                        marginLeft = `${index * FactorWidth}px`
                    else
                        marginLeft = (cellWidth - FactorWidth) * index / (totalFactors - 1 ) + 'px'
                    return {
                        detail,
                        detailInitials: detail[0],
                        marginLeft: marginLeft
                    }
                })
            } catch (error) {
                console.error(error)
                return this.sleepDetail.map((detail, index) => ({
                    detail,
                    detailInitials: detail[0],
                    marginLeft: '0'
                }))
            }
            
        },
        /**
         * Calculates day slot to display (eg. 2PM - 3PM) on the popoverf or the cell
         */
        daySlotDisplay() {
            return `${getTimeFromIndex(this.daySlot)} - ${getTimeFromIndex(this.daySlot + 1)}`
        },
        /**
         * Calculates if line before sleepStart has to be erased
         */
        isLineEraseBefore() {
            return this.sleepStart.length > 0 && !this.sleepStatus.before
        },
        /**
         * Calculates if line ater sleepEnd has to be erased
         */
        isLineEraseAfter() {
            return this.sleepEnd.length > 0 && !this.sleepStatus.after
        }
    },

    methods: {
        ...format,
        /**
         * Get percentage width of minutes if block is 60 minutes
         */
        getPercentageFromTime(sleepTime) {
            const timePercent = new Date(sleepTime).getMinutes() / 60 * 100
            return  `calc( ${timePercent}% - 6px)`
        }
    }

}
</script>

<style lang="scss" scoped>
@import "@/module/app/theme/color.scss";
// Colors for the Sleep Factors
$alcohol-color : #9b85e4;
$exercise-color: #5ebfe6;
$caffeine-color: #ea895a;
$nap-color: #7ed5a8;
$medicine-color: #e48ceb;

.factor-tooltip {
    background-color: inherit;
}

.sleep-cell {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    background-color: inherit;
    cursor: pointer;
    .detail {
        grid-area: 1 / 1 / 2 / 2;
        text-transform: uppercase;
        height: 16px;
        width: 16px;
        font-size: 14px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 100%;
        color: $white-color;
        z-index: 1;
    }
    .alcohol {
        background-color: $alcohol-color;
    }
    .exercise {
        background-color: $exercise-color;
    }
    .caffeine {
        background-color: $caffeine-color;
    }
    .nap {
        background-color: $nap-color;
    }
    .medicine {
        background-color: $medicine-color;
    }
    .sleep-event {
        grid-area: 1 / 1 / 2 / 2;
        width: 12px;
        height: 12px;
        margin-top: 2px;
        border-radius: 50%;
        z-index: 1;
    }
    .sleep-start {
        background-color: $vital-color;
    }
    .sleep-end {
        border: 1px solid $vital-color;
        background-color: #ffffff;
    }
    .line-erase-before {
        background: inherit;
        grid-area: 1 / 1 / 2 / 2;
        z-index: 1;

    }
    .line-erase-after {
        background-color: inherit;
        grid-area: 1 / 1 / 2 / 2;
        z-index: 1;
    }
}
.popover-content {
    font-size: 10px;
    .display-time {
        color: $grey-300;
    }
    .display-detail {
        color: $white-color;
        text-transform: capitalize;
    }
}
</style>

<style>
.el-popover.popper-factor {
    background-color: #333333;
    border: none;
    padding: 6px;
    padding-top: 12px;
}
.el-popper.popper-factor[x-placement^=bottom] .popper__arrow {
    border-bottom-color: #333333;
}
..el-popper.popper-factor[x-placement^=top] .popper__arrow {
    border-top-color: #333333;
}
.el-popper.popper-factor[x-placement^=bottom] .popper__arrow::after, .el-popper.popper-factor[x-placement^=top] .popper__arrow::after {
    border-bottom-color: #333333;
}
.el-popper.popper-factor[x-placement^=bottom] {
    margin-top: 6px;
}
</style>
